import { catchError, concat, filter, from, map, mergeMap, of, withLatestFrom } from 'rxjs'
import type { Epic } from '../../index.js'
import type { AdminSpinQuery } from '../../Slice/AdminSpin/AdminSpinSlice.js'
import { adminSpinSlice, cacheKey } from '../../Slice/AdminSpin/AdminSpinSlice.js'
// Imported directly rather than taken from the injected `deps`, unlike the other epics. The injection
// exists so an epic can be unit-tested against a mock repository; this one has no logic worth testing
// that way (the cache decisions live in the slice, which is tested), and adding AdminSpinRepository to
// the Repository barrel would put an axios/Promise module into a set that is otherwise rxjs-ajax.
import * as AdminSpinRepository from '../../../Repository/Admin/AdminSpinRepository.js'

/**
 * Dispatch one [AdminSpinQuery] to the repository call that answers it.
 *
 * A switch rather than a lookup table so the discriminated union narrows each variant — adding a query
 * kind without wiring it up is then a compile error, and the argument order of each `fetch*` is checked
 * rather than assumed.
 */
const runQuery = (query: AdminSpinQuery): Promise<unknown> => {
  switch (query.kind) {
    case 'runs':
      return AdminSpinRepository.fetchSpinRuns()
    case 'metrics':
      return AdminSpinRepository.fetchSpinMetrics(query.algorithmType, query.asOf, query.amplicon)
    case 'cohesion':
      return AdminSpinRepository.fetchSpinCohesion(query.algorithmType, query.asOf, query.amplicon)
    case 'leaderboard':
      return AdminSpinRepository.fetchSpinLeaderboard(query.algorithmType, query.asOf, query.amplicon, query.limit)
    case 'singletons':
      return AdminSpinRepository.fetchSpinHighIdentitySingletons(
        query.algorithmType,
        query.asOf,
        query.amplicon,
        query.minHitPercentage,
        query.limit
      )
    case 'reference':
      return AdminSpinRepository.fetchSpinReference(
        query.algorithmType,
        query.asOf,
        query.refKind,
        query.amplicon,
        query.orderBy,
        query.minHitPercentage
      )
    case 'referenceDetail':
      return AdminSpinRepository.fetchSpinReferenceDetail(
        query.algorithmType,
        query.asOf,
        query.refKind,
        query.refId,
        query.amplicon,
        query.limit
      )
    case 'compare':
      return AdminSpinRepository.fetchSpinCompare(query.a, query.b, query.amplicon)
    case 'drift':
      return AdminSpinRepository.fetchSpinDrift(query.runId, { rank: query.rank, source: query.source })
    case 'lineage':
      return AdminSpinRepository.fetchSpinLineage(query.algorithmType)
    case 'membershipFlow':
      return AdminSpinRepository.fetchSpinMembershipFlow(query.runId)
    case 'detail':
      return AdminSpinRepository.fetchSpinDetail(query.spinId)
  }
}

/**
 * The single fetch path for the admin SPIN panel: fetch-if-absent, keyed by [cacheKey].
 *
 * Skips the request when the key is already `ready` or in flight (`loading`), which is what turns
 * navigating between sub-pages from "re-run the multi-second query" into a state read. `error` entries
 * are NOT skipped, so a failed query retries next time the page asks rather than being stuck until
 * reload; `force` (the pages' Reload buttons) always re-fetches.
 *
 * `mergeMap` and not `switchMap`: several distinct queries are legitimately in flight together (a page
 * mounts `/metrics` and a heavy whole-set query at once) and cancelling one because another started
 * would strand it. Duplicate keys can't pile up because the in-flight one is already `loading` — the
 * `queryStarted` below is emitted synchronously, before the next action is filtered.
 */
export const fetchAdminSpinQueryEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(adminSpinSlice.actions.query.match),
    withLatestFrom(state$),
    filter(([action, state]) => {
      if (action.payload.force) return true
      const entry = state.adminSpin.entries[cacheKey(action.payload.query)]
      return entry === undefined || entry.status === 'error'
    }),
    mergeMap(([action]) => {
      const key = cacheKey(action.payload.query)
      return concat(
        of(adminSpinSlice.actions.queryStarted({ key })),
        from(runQuery(action.payload.query)).pipe(
          map((data) => adminSpinSlice.actions.queryFulfilled({ key, data })),
          catchError((error) =>
            of(
              adminSpinSlice.actions.queryFailed({
                key,
                error: error instanceof Error ? error.message : 'Request failed'
              })
            )
          )
        )
      )
    })
  )
